if (!customElements.get('ss-native-header')) {
  class SSNativeHeader extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.mobile = window.matchMedia('(max-width: 1100px)');
      this.hover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.drawerButton = this.querySelector('[data-ssn-drawer-toggle]');
      this.navigation = this.querySelector('.ssn-navigation');
      this.panes = [...this.querySelectorAll('[data-ssn-pane]')];
      this.inertBefore = new Map();
      this.overflowBefore = null;
      this.setAttribute('data-ready', '');
      this.drawerButton.addEventListener('click', () => this.toggleDrawer(), {signal});
      this.querySelector('[data-ssn-shade]').addEventListener('click', () => this.closeEverything(true), {signal});
      this.addEventListener('toggle', e => this.onToggle(e), {capture:true, signal});
      this.addEventListener('keydown', e => this.onKeydown(e), {signal});
      this.addEventListener('click', e => {
        const close = e.target.closest('[data-ssn-close-pane]');
        if (close) {
          const pane = close.closest('[data-ssn-pane]');
          if (pane) {pane.open = false; pane.querySelector('summary').focus();}
        }
        if (e.target.closest('a[href]') && this.hasAttribute('data-drawer-open')) this.closeDrawer(false);
        if (e.target.closest('#cart-icon-bubble')) this.closeEverything(false);
      }, {capture:true, signal});
      document.addEventListener('click', e => {if (!this.contains(e.target)) this.closeEverything(false);}, {signal});
      document.addEventListener('keydown', e => {
        if (this.hasAttribute('data-drawer-open') && e.key === 'Tab') this.trapDrawerFocus(e);
      }, {signal});
      this.panes.forEach(pane => {
        pane.addEventListener('pointerenter', () => {
          clearTimeout(this.closeTimer);
          if (this.mobile.matches || !this.hover.matches || !pane.matches('[data-ssn-main-menu]')) return;
          this.openTimer = setTimeout(() => {pane.open = true;}, 140);
        }, {signal});
        pane.addEventListener('pointerleave', () => {
          clearTimeout(this.openTimer);
          if (this.mobile.matches || !this.hover.matches) return;
          this.closeTimer = setTimeout(() => {
            if (!pane.contains(document.activeElement)) pane.open = false;
          }, 220);
        }, {signal});
      });
      this.querySelectorAll('.ssn-group').forEach(group => {
        const trigger = group.querySelector('summary');
        trigger.setAttribute('aria-expanded',String(group.open));
        trigger.addEventListener('click', event => {
          if (!this.mobile.matches) {event.preventDefault();group.open = true;}
        }, {signal});
        trigger.addEventListener('pointerenter', () => {
          if (!this.mobile.matches && this.hover.matches) group.open = true;
        }, {signal});
      });
      this.addEventListener('focusout', () => {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          if (!this.contains(document.activeElement) && !this.hasAttribute('data-drawer-open')) this.closeEverything(false);
        }, 0);
      }, {signal});
      this.onMediaChange = () => {this.closeEverything(false);this.updateHeight();};
      this.mobile.addEventListener('change', this.onMediaChange);
      this.resize = new ResizeObserver(() => this.updateHeight());
      this.resize.observe(this);
      this.updateHeight();
    }

    disconnectedCallback() {
      this.closeDrawer(false);
      this.abort?.abort();
      this.abort = null;
      this.resize?.disconnect();
      this.mobile?.removeEventListener('change', this.onMediaChange);
      [this.openTimer,this.closeTimer,this.focusTimer].forEach(clearTimeout);
    }

    updateHeight() {
      const height = Math.round(this.getBoundingClientRect().height);
      this.style.setProperty('--ssn-header-height', `${height}px`);
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }

    updatePanelHeight(container) {
      if (!container || this.mobile.matches) return;
      const panel = container.querySelector('.ssn-group[open] .ssn-group-panel');
      const leftHeight = [...container.querySelectorAll('.ssn-group-trigger')].reduce((total, trigger) => total + trigger.getBoundingClientRect().height, 0);
      container.style.setProperty('--ssn-panel-height', `${Math.ceil(Math.max(leftHeight, panel?.scrollHeight || 0, 464))}px`);
    }

    onToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      const summary = details.querySelector(':scope > summary');
      if (summary) summary.setAttribute('aria-expanded', String(details.open));
      if (details.matches('[data-ssn-pane]')) {
        if (details.open) this.panes.forEach(p => {if (p !== details) p.open = false;});
        this.toggleAttribute('data-ssn-open', this.panes.some(p => p.open));
        if (details.open) {
          requestAnimationFrame(() => {
            this.updatePanelHeight(details.querySelector('.ssn-groups'));
            if (details.classList.contains('ssn-search')) details.querySelector('input[type="search"]')?.focus();
          });
        }
      }
      if (details.classList.contains('ssn-group')) {
        const container = details.closest('.ssn-groups');
        if (details.open) container.querySelectorAll('.ssn-group').forEach(g => {if (g !== details) g.open = false;});
        requestAnimationFrame(() => {
          if (!this.mobile.matches && !container.querySelector('.ssn-group[open]')) details.open = true;
          this.updatePanelHeight(container);
        });
      }
    }

    toggleDrawer() {
      if (this.hasAttribute('data-drawer-open')) this.closeDrawer(true);
      else this.openDrawer();
    }

    openDrawer() {
      this.closeEverything(false);
      this.setAttribute('data-drawer-open','');
      this.drawerButton.setAttribute('aria-expanded','true');
      this.drawerButton.setAttribute('aria-label',this.drawerButton.dataset.closeLabel);
      this.overflowBefore = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.querySelectorAll('main, footer').forEach(element => {
        if (!this.contains(element)) {this.inertBefore.set(element,element.inert);element.inert = true;}
      });
      requestAnimationFrame(() => this.navigation.querySelector('summary,a[href]')?.focus());
    }

    closeDrawer(focus) {
      if (!this.hasAttribute('data-drawer-open')) return;
      this.removeAttribute('data-drawer-open');
      this.drawerButton.setAttribute('aria-expanded','false');
      this.drawerButton.setAttribute('aria-label',this.drawerButton.dataset.openLabel);
      if (this.overflowBefore !== null) document.body.style.overflow = this.overflowBefore;
      this.overflowBefore = null;
      this.inertBefore.forEach((before,element) => {element.inert = before;});
      this.inertBefore.clear();
      if (focus) this.drawerButton.focus();
    }

    closeEverything(focus) {
      clearTimeout(this.openTimer);clearTimeout(this.closeTimer);
      this.panes?.forEach(pane => {pane.open = false;});
      this.removeAttribute('data-ssn-open');
      this.closeDrawer(focus);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        const pane = event.target.closest('[data-ssn-pane][open]');
        if (pane) {event.preventDefault();event.stopPropagation();pane.open = false;pane.querySelector('summary').focus();}
        else if (this.hasAttribute('data-drawer-open')) {event.preventDefault();this.closeEverything(true);}
      }
      const groupTrigger = event.target.closest('.ssn-group-trigger');
      if (groupTrigger && ['ArrowDown','ArrowUp','Home','End'].includes(event.key)) {
        event.preventDefault();
        const triggers = [...groupTrigger.closest('.ssn-groups').querySelectorAll('.ssn-group-trigger')];
        let index = triggers.indexOf(groupTrigger);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = triggers.length - 1;
        else index = (index + (event.key === 'ArrowDown' ? 1 : -1) + triggers.length) % triggers.length;
        triggers[index].parentElement.open = true;triggers[index].focus();
      }
      const mainSummary = event.target.closest('summary.ssn-nav-link');
      if (mainSummary && event.key === 'ArrowDown') {
        event.preventDefault();mainSummary.parentElement.open = true;
        requestAnimationFrame(() => mainSummary.parentElement.querySelector('.ssn-group-trigger,.ssn-links a,.ssn-service-links a')?.focus());
      }
    }

    trapDrawerFocus(event) {
      const controls = [...this.querySelectorAll('a[href],button,summary,input,select,[tabindex="0"]')].filter(e => e.getClientRects().length && !e.disabled && e.tabIndex !== -1);
      const first = controls[0], last = controls[controls.length-1];
      if (!first) return;
      if (event.shiftKey && (document.activeElement === first || !this.contains(document.activeElement))) {event.preventDefault();last.focus();}
      else if (!event.shiftKey && document.activeElement === last) {event.preventDefault();first.focus();}
    }
  }
  customElements.define('ss-native-header',SSNativeHeader);
}
